function LegalShell({ title, updated, children, testid }) {
  return (
    <main id="main" className="relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -right-28 -top-28 h-[320px] w-[320px] rounded-full bg-brand-aqua/60" />
        <div className="absolute -left-24 top-[38%] h-[200px] w-[200px] rounded-full bg-brand-peach/50" />
      </div>
      <div className="symp-container relative py-14 md:py-20">
        <article className="mx-auto max-w-[880px]" data-testid={testid}>
          <h1 className="section-title text-brand-teal">{title}</h1>
          <p className="mt-3 text-[16px] font-bold text-brand-ink/70">
            {updated}
          </p>
          <div className="mt-10 space-y-10 text-[17px] font-medium leading-[1.65] text-brand-ink md:text-[18px]">
            {children}
          </div>
        </article>
      </div>
    </main>
  );
}

function LegalSection({ heading, children }) {
  return (
    <section>
      <h2 className="mb-3 text-[24px] font-extrabold text-brand-teal md:text-[28px]">
        {heading}
      </h2>
      {children}
    </section>
  );
}

function LegalList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-[10px] h-2 w-2 shrink-0 rounded-full bg-brand-coral"
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function EmailLink() {
  return (
    <a
      href="mailto:symptrackhealth@gmail.com"
      className="font-bold text-brand-teal underline underline-offset-4"
      data-testid="legal-email-link"
    >
      symptrackhealth@gmail.com
    </a>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy"
      updated="Last updated: October 2026"
      testid="privacy-page"
    >
      <p>
        This policy describes how SympTrack handles information through its
        public marketing website.
      </p>
      <LegalSection heading="Information You Provide">
        <p>
          If you contact SympTrack through the website contact form, the
          submission may include:
        </p>
        <LegalList
          items={[
            "Name",
            "Email",
            "Organization",
            "Reason for contacting SympTrack",
            "Message",
          ]}
        />
      </LegalSection>
      <LegalSection heading="How Information Is Used">
        <p>Information submitted through the contact form is used to:</p>
        <LegalList
          items={[
            "Respond to inquiries",
            "Communicate about demos",
            "Discuss clinical and academic partnerships",
            "Respond to investment inquiries",
            "Discuss collaborations",
            "General communication",
            "Maintain and improve website communication",
          ]}
        />
      </LegalSection>
      <LegalSection heading="Website and Technical Information">
        <p>
          Ordinary technical information may be processed as necessary to
          operate and secure the website. What is processed can depend on the
          hosting and infrastructure used to run the website at any given
          time.
        </p>
      </LegalSection>
      <LegalSection heading="Health Information">
        <p>
          The public marketing website contact form is not intended for
          medical records, medical emergencies, diagnoses, detailed health
          information or other sensitive clinical information. Please do not
          submit sensitive medical information through the public contact
          form.
        </p>
      </LegalSection>
      <LegalSection heading="Third Party Services">
        <p>
          The website links to external scheduling and support payment
          services. Those services maintain their own privacy practices, which
          are separate from this policy.
        </p>
      </LegalSection>
      <LegalSection heading="Children">
        <p>
          The public website is not intentionally designed to collect personal
          information from children through its contact form.
        </p>
      </LegalSection>
      <LegalSection heading="Data Choices">
        <p>
          If you have voluntarily submitted information through the website
          and have a question about it, you may contact SympTrack at{" "}
          <EmailLink />
          .
        </p>
      </LegalSection>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell
      title="Terms of Use"
      updated="Last updated: October 2026"
      testid="terms-page"
    >
      <LegalSection heading="About This Website">
        <p>
          This public website provides information about SympTrack, its
          mission, product concept and opportunities to contact, support or
          collaborate with SympTrack.
        </p>
      </LegalSection>
      <LegalSection heading="Informational Purpose">
        <p>
          Public website content is provided for general informational
          purposes.
        </p>
      </LegalSection>
      <LegalSection heading="Healthcare Disclaimer">
        <p>
          The public SympTrack marketing website does not provide medical
          diagnosis or treatment. Website information is not a substitute for
          professional medical advice, diagnosis or treatment.
        </p>
      </LegalSection>
      <LegalSection heading="Emergency Disclaimer">
        <p>
          The public website and contact form are not emergency services and
          are not monitored for medical emergencies. For emergencies, contact
          appropriate emergency services.
        </p>
      </LegalSection>
      <LegalSection heading="Contact Form">
        <p>
          Visitors should not submit medical records, highly sensitive health
          information, passwords, financial account information or other
          unnecessary sensitive information through the contact form.
        </p>
      </LegalSection>
      <LegalSection heading="External Services">
        <p>
          External scheduling and payment services operate under their own
          terms and policies.
        </p>
      </LegalSection>
      <LegalSection heading="Support SympTrack">
        <p>
          Support SympTrack provides a way to financially support the
          development and work of SympTrack. Investment discussions occur
          separately through direct communication.
        </p>
      </LegalSection>
      <LegalSection heading="Intellectual Property">
        <p>
          SympTrack&trade; branding, original website content, Sophie artwork,
          logos and other original materials may be protected under applicable
          intellectual property laws.
        </p>
      </LegalSection>
      <LegalSection heading="Website Availability">
        <p>
          The site may be changed, updated, interrupted or temporarily
          unavailable.
        </p>
      </LegalSection>
      <LegalSection heading="Changes">
        <p>
          These terms may be updated as SympTrack and the website evolve.
        </p>
      </LegalSection>
      <LegalSection heading="Contact">
        <p>
          Questions about these terms can be sent to <EmailLink />.
        </p>
      </LegalSection>
    </LegalShell>
  );
}

export function AccessibilityPage() {
  return (
    <LegalShell
      title="Accessibility"
      updated="SympTrack is committed to continuous improvement"
      testid="accessibility-page"
    >
      <p>
        SympTrack aims to make its public website usable by as many people as
        possible, and accessibility improvements are ongoing.
      </p>
      <LegalSection heading="What We Work Toward">
        <LegalList
          items={[
            "Keyboard navigation",
            "Visible focus states",
            "Readable typography",
            "Responsive layout",
            "Meaningful image alt text",
            "Reduced motion support",
            "Color contrast",
            "Accessible form labels",
            "Accessible validation",
          ]}
        />
      </LegalSection>
      <LegalSection heading="Tell Us About a Difficulty">
        <p>
          If you experience difficulty using the SympTrack website or
          accessing information, please contact us at <EmailLink />.
        </p>
        <p className="mt-4">
          When you reach out, it helps to identify the page or feature you had
          difficulty accessing.
        </p>
      </LegalSection>
      <p>
        SympTrack has not completed a formal third party accessibility audit,
        so no formal certification is claimed.
      </p>
    </LegalShell>
  );
}
